import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./AdvertisePage.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import BannerAds from "./BannerAds";

const AdvertisePage = () => {
  const [previewAd, setPreviewAd] = useState(null);
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);

  const COLORS = ["#6332ff", "#00ffc8", "#f472b6", "#60a5fa", "#a78bfa", "#fbbf24"];

  const makeParticle = useCallback((W, H) => ({
    x: Math.random() * W,
    y: Math.random() * H,
    vx: (Math.random() - 0.5) * 0.4,
    vy: (Math.random() - 0.5) * 0.4,
    r: Math.random() * 1.8 + 0.4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    alpha: Math.random() * 0.6 + 0.2,
    pulse: Math.random() * Math.PI * 2,
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root) return;
    const ctx = canvas.getContext("2d");
    let W = root.offsetWidth;
    let H = Math.max(root.offsetHeight, window.innerHeight);
    let particles = [];

    const resize = () => {
      W = root.offsetWidth;
      H = Math.max(root.offsetHeight, window.innerHeight);
      canvas.width = W;
      canvas.height = H;
    };
    resize();
    particles = Array.from({ length: 90 }, () => makeParticle(W, H));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.018;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        const pa = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = pa;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = particles[i].color;
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - dist / 100) * 0.12;
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();

    const ro = new ResizeObserver(resize);
    ro.observe(root);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [makeParticle]);

  const ads = [
    {
      type: "Instagram / YouTube",
      text: "Follow our tech channel for programming tutorials and coding tips.",
      link: "https://youtu.be/B5vzO_BuS3w?list=RDB5vzO_BuS3w",
    },
    {
      type: "Banner / Image",
      image: "blog.png",
      link: "https://youtu.be/B5vzO_BuS3w?list=RDB5vzO_BuS3w",
    },
    {
      type: "Video",
      video: "https://www.w3schools.com/html/mov_bbb.mp4",
    },
  ];

  const AD_CARDS = [
    {
      icon: "📱",
      title: "Instagram / YouTube Promotion",
      desc: "Promote your Instagram page or YouTube videos to our blog community. Share a short promotional text along with your channel or video link. Your promotion will appear in a clean and engaging format.",
      color: "#f472b6",
      adIdx: 0,
    },
    {
      icon: "🖼️",
      title: "Banner / Image Ads",
      desc: "Promote your brand or website using attractive banners or images. When users click the banner, they will be redirected to your provided link such as your website, blog, or social media page.",
      color: "#60a5fa",
      adIdx: 1,
    },
    {
      icon: "🎬",
      title: "Video Ads",
      desc: "Promote your product, brand, or service with short video ads. The video should be around 15–20 seconds and will be displayed inside the platform so users can watch your promotional content.",
      color: "#a78bfa",
      adIdx: 2,
    },
  ];

  const LEGAL_ITEMS = [
    "Advertisement is a paid service. We do not force anyone to advertise. You can choose to advertise only if you are interested.",
    "The price depends on the advertisement type (Text, Image or Video) and is shown on the Advertise With Us page before payment, together with the estimated impressions and the maximum campaign duration. Estimated impressions are an estimate, not a guarantee. A campaign ends when its minimum estimated impressions are delivered or its maximum duration ends, whichever comes first.",
    "To ensure transparency, the My Campaigns dashboard on the Advertise With Us page shows each campaign's status, minimum impression target, delivered impressions, duration used and maximum duration.",
    "You can submit different types of advertisements: text promotions with YouTube/Instagram links, image/banner ads with redirect links, or short video ads for brand promotion.",
    "Important: We do not promise increases in followers, subscribers, likes, or engagement. Our service only shows your advertisement inside our app. We only provide placement, not boosting.",
    "Advertisement payments are non-refundable once the ad campaign has started.",
    "Advertisement will be started only after payment confirmation and admin approval.",
    "We do not allow ads containing illegal, adult, harmful, misleading, betting, hacking, or inappropriate content. We reserve the right to reject or remove any advertisement.",
    "Ad views are counted only when the ad is displayed inside the app. Clicks and engagement depend on user interest.",
    "In case of server issues or maintenance, ad display may be delayed. Remaining views will be adjusted later.",
    "By submitting an advertisement, you agree to all advertising rules and policies of this platform.",
  ];

  return (
    <div className="ap-root" ref={rootRef}>
      <canvas className="ap-canvas" ref={canvasRef} />
      <div className="ap-aurora" />

      <Navbar />

      {/* ─── HERO ─── */}
      <section className="ap-hero">
        <BannerAds/>
        <div className="ap-hero-label">✦ advertising platform</div>
        <h1 className="ap-hero-title">Advertise on<br />TheBlogSphere</h1>
        <p className="ap-hero-sub">
          Promote your brand, blog, YouTube channel, Instagram page, or product
          to a growing audience of readers and creators.
        </p>
        <Link to="/advertise" className="ap-hero-cta">
          Start Advertising →
        </Link>
      </section>

      {/* ─── STATS BANNER ─── */}
      <div className="ap-stats-row">
        {[
          { num: "7:1", label: "blogs per ad" },
          { num: "3",   label: "ad formats" },
          { num: "24h", label: "fast activation" },
        ].map((s) => (
          <div className="ap-stat" key={s.label}>
            <span className="ap-stat-num">{s.num}</span>
            <span className="ap-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ─── AD TYPE CARDS ─── */}
      <section className="ap-section">
        <div className="ap-section-label">✦ ad formats</div>
        <h2 className="ap-section-title">Choose Your Ad Type</h2>
        <div className="ap-cards-grid">
          {AD_CARDS.map((card) => (
            <div className="ap-card" key={card.title} style={{ "--card-color": card.color }}>
              <div className="ap-card-scanline" />
              <div className="ap-card-glow" />
              <div className="ap-card-icon">{card.icon}</div>
              <h3 className="ap-card-title">{card.title}</h3>
              <p className="ap-card-desc">{card.desc}</p>
              <button className="ap-preview-btn" onClick={() => setPreviewAd(ads[card.adIdx])}>
                Preview Example →
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className="ap-contact">
        <div className="ap-contact-scanline" />
        <div className="ap-contact-glow" />
        <div className="ap-section-label" style={{ color: "#00ffc8", borderColor: "rgba(0,255,200,0.3)", background: "rgba(0,255,200,0.05)" }}>✦ get started</div>
        <h2 className="ap-contact-title">Start Promoting Today</h2>
        <p className="ap-contact-sub">
          Interested in promoting your brand, blog, or channel on TheBlogSphere?
          Contact us directly on WhatsApp — we'll set up your campaign fast.
        </p>
        <a
          href="https://wa.me/918095996203?text=Hello%20Team,%20I%20would%20like%20to%20advertise%20on%20TheBlogSphere"
          target="_blank"
          rel="noopener noreferrer"
          className="ap-whatsapp-btn"
        >
          📲 Chat on WhatsApp
        </a>
        <div className="ap-ad-types">
          <span className="ap-ad-type-pill">📝 Text Ads</span>
          <span className="ap-ad-type-pill">🖼️ Banner / Image</span>
          <span className="ap-ad-type-pill">🎬 Short Video</span>
        </div>
      </section>

      {/* ─── POLICY ─── */}
      <section className="ap-policy">
        <div className="ap-policy-scanline" />
        <div className="ap-section-label" style={{ color: "#fbbf24", borderColor: "rgba(251,191,36,0.3)", background: "rgba(251,191,36,0.05)" }}>✦ transparency</div>
        <h2 className="ap-policy-title">Advertising Policy</h2>
        <ul className="ap-policy-list">
          {LEGAL_ITEMS.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
          <li>
            Example of Ads Analytics page:
            <div className="ap-analytics-wrap">
              <img src="/img.jpeg" alt="Ads Analytics Preview" className="ap-analytics-img" />
            </div>
          </li>
        </ul>
      </section>

      {/* ─── PREVIEW MODAL ─── */}
      {previewAd && (
        <div className="ap-modal-overlay" onClick={() => setPreviewAd(null)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-scanline" />
            <div className="ap-modal-label">✦ ad preview</div>
            <h3 className="ap-modal-title">Advertisement Preview</h3>

            {previewAd.text && <p className="ap-modal-text">{previewAd.text}</p>}

            {previewAd.image && (
              previewAd.link ? (
                <a href={previewAd.link} target="_blank" rel="noreferrer">
                  <img src={previewAd.image} alt="preview" className="ap-modal-img" />
                </a>
              ) : (
                <img src={previewAd.image} alt="preview" className="ap-modal-img" />
              )
            )}

            {previewAd.video && (
              <video controls className="ap-modal-video">
                <source src={previewAd.video} type="video/mp4" />
              </video>
            )}

            <div className="ap-modal-btns">
              {previewAd.link && (
                <a href={previewAd.link} target="_blank" rel="noreferrer" className="ap-modal-link-btn">
                  Visit Link
                </a>
              )}
              <button className="ap-modal-close-btn" onClick={() => setPreviewAd(null)}>
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdvertisePage;